import { LogDebug } from 'utils/logging';
import { LogError } from 'utils/logging/LogError';
import { ErrorPriority } from 'types';
import { wordpressSuperFetch, triadSuperFetch } from 'utils/superFetch';
import { TRIAD_PROXY_ROUTE, WP_API_URL } from 'app-requests/apiConstants';
import { getDOMFieldValue, isValidZip } from 'utils/formValuesUtils';
import { formValuesToRequestArr } from 'utils/form-utils/formValuesToRequestArr';
import {
  getAllCookies,
  getStoredQueryParams,
  getUserSessionId,
  getPageViewId,
} from 'utils/analyticsHelpers';
import { QUERY_PARAMS, QUESTION_IDS } from 'consts';
import { getImageUrlMap } from 'utils/imageHelpers';
import { extractUserTrackingFilters } from 'utils/session/extractUserTrackingFilters';
import { getTriadBackendAPIDomain } from 'configs';
import { APITransformError } from 'utils/errors/APITransformError';
import { getExperimentInfoForTmsLogging } from 'utils/ab-testing/getActiveTestingVariant';
import { parseWpQuestionnaires } from './transformers/parseWpQuestionnaires';

const { SHARED_EDU_ID, SHARED_SESSION_ID } = QUERY_PARAMS;

/**
 * @summary this is used to get the questionnaire for a micro site
 * @param {String} domain - the domain of this microsite
 * @param {String} variant - the AB test variant to use
 * @param {Object} questionBankMap - All Questions in TriadSystem
 * @param {Object} redisHelpers - All Questions in TriadSystem
 */
export async function getQuestionnaires(args) {
  const { domain, schoolCode, redisHelpers } = args;
  if (!schoolCode || !domain) {
    LogError('getQuestionnaires: No School Code Found in Wordpress Configs', {
      priority: ErrorPriority.ONE,
      description: `domain: ${domain}`,
    });
  }

  const questionnaires = await redisHelpers.getQuestionnaires();
  if (questionnaires) {
    return Promise.resolve(questionnaires);
  }

  const [apiError, response] = await wordpressSuperFetch(
    `${WP_API_URL}/triad_questionnaire`,
    {
      timeout: 3000,
      retry: {
        maxAttempts: 3,
        waitTimeBetweenFails: 2000,
      },
      queryParams: {
        frontendDomain: domain,
      },
    }
  );

  if (apiError) {
    throw apiError;
  }

  try {
    const parsedResponse = parseWpQuestionnaires(response, args);
    redisHelpers.setQuestionnaires(parsedResponse);
    return parsedResponse;
  } catch (error) {
    throw new APITransformError(error.message, 'getQuestionnaires');
  }
}

/**
 * @summary use this to log current progress for user's session so we can get it back in the getProfile API
 * @param {Object} formValues - current form state
 * @param {Object} fieldNameMap - map of all field names
 * @param {String} config.schoolCode - this comes from wordpress to tell us the code for this school
 * @param {String} config.variant - the questionnaire version we are showing user, A/B test
 * @param {Object} linkedSessionFormValues - form values that could have come from another session
 * @param {number} lastQuestionAnswered - id of the last question answered
 * @param {?String=} endpoint - the endpoint to hit
 */
let didAlert = false;
async function _logProgress(
  formValues,
  fieldNameMap,
  { schoolCode, variant },
  linkedSessionFormValues,
  lastQuestionAnswered,
  endpoint = '/questionLog'
) {
  const userSessionId = getUserSessionId();
  const { queryParamMap } = getStoredQueryParams();

  if (!userSessionId && !didAlert) {
    didAlert = true;
    LogError('No Session Cookie Found When Logging Question', {
      schoolCode,
      variant,
      formAnsweredQuestion: Object.keys(formValues).join(', '),
    });
    return Promise.resolve({});
  }

  if (userSessionId && !getPageViewId()) {
    LogDebug('No Page View Id found but a user session was detected');
  }

  const [apiError, response] = await triadSuperFetch(
    `${TRIAD_PROXY_ROUTE}${endpoint}`,
    {
      method: 'POST',
      timeout: 5000,
      body: {
        schoolCode,
        templateName: variant,
        questionReplies: formValuesToRequestArr(
          formValues,
          fieldNameMap,
          false,
          linkedSessionFormValues
        ),
        currentQuestionId: lastQuestionAnswered,
        [SHARED_EDU_ID]: queryParamMap[SHARED_EDU_ID],
        [SHARED_SESSION_ID]: queryParamMap[SHARED_SESSION_ID],
      },
    }
  );

  if (apiError) {
    return apiError;
  }

  return response;
}

/**
 * @summary a proxy function for _logProgress so that we may call the last one
 * @param  {...any} args - see above function _logProgress
 */
let logProgressInterval = null;
export function logProgress(...args) {
  clearInterval(logProgressInterval);

  // if we have a user session then just call the API
  if (getUserSessionId()) {
    return _logProgress(...args);
  }

  logProgressInterval = setInterval(() => {
    if (getUserSessionId()) {
      clearInterval(logProgressInterval);
      _logProgress(...args);
    }
  }, 1500);

  return Promise.resolve({});
}

/**
 * @summary used to track needed session ids for google to track offsite conversions
 */
let isCurrentSessionTracked = false;

export function forTestingOnlyClearIsCurrentSessionTracked() {
  isCurrentSessionTracked = false;
}

export function getUsersGoogleSessionIds() {
  const cookies = getAllCookies();
  return cookies.keys
    .filter((key) => key.match(/^(_ga|_gcl_)/))
    .reduce((keyValueArray, key) => {
      keyValueArray.push({
        name: key,
        value: cookies.map[key],
      });

      return keyValueArray;
    }, []);
}

export async function trackUsersGoogleSessionIds() {
  if (isCurrentSessionTracked) {
    return Promise.resolve();
  }

  isCurrentSessionTracked = true;

  const [apiError, response] = await triadSuperFetch(
    `${TRIAD_PROXY_ROUTE}/SetUserMetadata`,
    {
      method: 'POST',
      timeout: 3000,
      body: {
        metadata: getUsersGoogleSessionIds(),
      },
    }
  );

  if (apiError) {
    return apiError;
  }

  return response;
}
export async function trackUsersExperimentInfo() {
  const [apiError, response] = await triadSuperFetch(
    `${TRIAD_PROXY_ROUTE}/SetUserMetadata`,
    {
      method: 'POST',
      timeout: 3000,
      body: {
        metadata: getExperimentInfoForTmsLogging(),
      },
      retry: {
        maxAttempts: 3,
        waitTimeBetweenFails: 2000,
      },
    }
  );

  if (apiError) {
    return apiError;
  }

  return response;
}

/**
 * @summary used to validate the user's zip code
 */
export async function getDataForZip({ zip }) {
  if (!isValidZip(zip)) {
    return Promise.resolve({ isValid: false });
  }

  const [apiError, response] = await triadSuperFetch(
    `${TRIAD_PROXY_ROUTE}/zipCheck`,
    {
      method: 'POST',
      body: {
        zipCode: zip,
      },
      timeout: 5000,
      cache: {
        type: 'memory',
        key: `zipCheck-${zip}`,
      },
    }
  );

  if (apiError) {
    return { isValid: true };
  }

  return {
    ...response,
    isValid: !!(response.state && response.city && zip === response.zipCode),
    suggestion: zip !== response.zipCode ? response.zipCode : '',
  };
}

/**
 * @summary function to run to validate a given phone number
 */

export async function validatePhone(phone) {
  const [apiError, response] = await triadSuperFetch(
    `${TRIAD_PROXY_ROUTE}/npaNxxCheck`,
    {
      method: 'POST',
      body: {
        phoneNumber: phone,
      },
      timeout: 5000,
      cache: {
        type: 'memory',
        key: `npaNxxCheck-${phone}`,
      },
    }
  );

  if (apiError) {
    return { isValid: true };
  }

  return { isValid: !!response.phoneNumber };
}

export function extractAndSortTaxonomy(children, level = 1) {
  return children.map((child) => ({
    level,
    orderBy: child.orderBy || null,
    value: child.value,
    label: child.label,
    children: extractAndSortTaxonomy(
      child.parentCategories || child.categories || [],
      level + 1
    ),
  }));
}

function parseSchoolDescription(desc) {
  if (!desc) {
    return 'Description not available please check back soon.';
  }

  return desc.map((txt) => `<p>${txt}</p>`).join();
}

async function getMicroPortalPostLeadSubmitAdditionalSchoolResults() {
  const [apiError, response] = await triadSuperFetch(
    `${TRIAD_PROXY_ROUTE}/microportal/GetMPAdditionalSchoolListings`,
    {
      method: 'POST',
      timeout: 3000,
    }
  );

  if (apiError) {
    throw apiError;
  }

  const { Listings, PhoneNumber } = response;

  return {
    phoneNumber: PhoneNumber,
    listings: Listings?.map((result) => {
      return {
        schoolGuid: result.value,
        schoolCode: result.schoolCode,
        schoolName: result.schoolName,
        impressionGuid: result.impressionGuid,
        label: result.label,
        rating: result.rating,
        description: parseSchoolDescription(result.schoolDesc),
        programs: result.programOptions[0]?.options,
        schoolLogo: getImageUrlMap(result.schoolImages),
      };
    }),
  };
}

export async function getMicroPortalPostLeadSubmitQuestionnaire({
  schoolCode,
}) {
  const schoolResults =
    await getMicroPortalPostLeadSubmitAdditionalSchoolResults();

  const questions = schoolResults.listings.map((school) => {
    return {
      grouping: 1,
      fieldGroupName: 'additionalResults',
      // TODO: [T1-10233] we should clean up core form logic to only need questionId or id and normalize the parsed backend question bank response respectively
      questionId: school.schoolGuid,
      id: school.schoolGuid,
      label: school.label,
      name: school?.schoolGuid?.toString(),
      type: 'SELECTION_CARD',
      required: false,
      isPii: false,
      options: school.programs,
      dependency: {},
      meta: {
        description: school.description,
        rating: school.rating,
        schoolLogo: school.schoolLogo,
        schoolName: school.schoolName,
        impressionGuid: school.impressionGuid,
      },
    };
  });

  questions.push({
    grouping: 1,
    questionId: QUESTION_IDS.MICRO_PORTAL_DYNAMIC_TCPA,
    id: QUESTION_IDS.MICRO_PORTAL_DYNAMIC_TCPA,
    label: 'second_page_tcpa',
    name: 'second_page_tcpa',
    type: 'ADDITIONAL_SCHOOLS_DISCLAIMER',
    required: true,
    isPii: false,
    options: [],
    meta: { phoneNumber: schoolResults?.phoneNumber },
  });

  return {
    schoolCode,
    variant: null,
    id: 'microPortalQuestionnaire',
    subTitle: null,
    title: null,
    stepsCount: 1,

    steps: [
      {
        questions,
        id: 1,
        trueStepIndex: 0,
        title: null,
        subTitle: null,
        heading: null,
        progressMeter: null,
        groupLabel: 'You Might Also Be Interested In',
      },
    ],
  };
}

/*
 * @param {String} schoolCode - the school code for the current school
 */
// TODO: [T1-11350] Rename from fetchFloodlightActivityValues to fetchAvailableAttributionValues
export async function fetchFloodlightActivityValues(schoolCode, cookies) {
  if (!schoolCode) {
    LogError('fetchFloodlightActivityValues: No School Code');
    return Promise.resolve({});
  }

  const [apiError, response] = await triadSuperFetch(
    `${getTriadBackendAPIDomain(cookies)}/GetCampaignManagerActivityValues`,
    {
      method: 'POST',
      body: { schoolCode },
      timeout: 5000,
      retry: {
        maxAttempts: 3,
        waitTimeBetweenFails: 1500,
      },
    }
  );

  if (apiError) {
    if (process.env.NODE_ENV === 'development') {
      LogError('fetchFloodlightActivityValues');
      return { eventValues: [] };
    }
    throw apiError;
  }

  try {
    return {
      eventValues: response?.tagEvents?.map(extractUserTrackingFilters) || [],
    };
  } catch (error) {
    throw new APITransformError(error.message, 'fetchFloodlightActivityValues');
  }
}

export async function getLeadEvalToken(
  formValues,
  fieldNameMap,
  schoolCode,
  metaData = {}
) {
  const { isClickUser } = metaData;

  const [apiError, response] = await triadSuperFetch(
    `${TRIAD_PROXY_ROUTE}/getLeadEvalToken`,
    {
      method: 'POST',
      timeout: 10000,
      cache: {
        type: 'memory',
        key: JSON.stringify(formValues),
      },
      retry: {
        maxAttempts: 3,
        waitTimeBetweenFails: 1000,
      },
      body: {
        schoolCode,
        trustedFormUrl: getDOMFieldValue('xxTrustedFormCertUrl_0'),
        leadId: getDOMFieldValue('leadid_token'),
        questionReplies: formValuesToRequestArr(formValues, fieldNameMap),
        isClickUser,
      },
    }
  );

  if (apiError) {
    throw apiError;
  }

  if (!response.leadEvalToken) {
    throw new Error('No lead eval token returned');
  }

  return { leadEvalToken: response.leadEvalToken };
}
